// Printable Field Time Report pages — fixed 816×1056 px (US letter @ 96dpi),
// light print design captured by html-to-image and composed into a PDF.
//
// Content rules (hard): positions, innings, and playing-time facts ONLY.
// No batting/pitching stats, no OVR/xOVR, no evaluation, no other players.

import React from 'react';
import {
  FieldTimeReportData,
  GameFieldLogRow,
  POSITION_LABELS,
} from '../../lib/field-time-report';
import { formatInningsOuts } from '../../lib/gc-innings';

// Innings are stored as decimal thirds (one out = 1/3) but must always be SHOWN
// in baseball notation: "11.1" is eleven innings and one out, and there is no
// such thing as .3 or higher. Every innings value on these pages goes through
// this — a raw float prints as "11.333333333333334" on a report a parent reads.
const fmtIP = formatInningsOuts;

export const GAME_LOG_ROWS_PER_PAGE = 22;

// Validated pair (CVD-safe on white, ≥3:1 contrast): infield blue / outfield green.
const IF_COLOR = '#2563EB';
const OF_COLOR = '#059669';
const INK = '#141A21';
const SECONDARY = '#5A6672';
const LINE = '#E3E7EB';
const TRACK = '#F1F3F5';

const PAGE_STYLE: React.CSSProperties = {
  width: 816,
  height: 1056,
  backgroundColor: '#FFFFFF',
  color: INK,
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  display: 'flex',
  flexDirection: 'column',
  padding: '56px 64px 40px',
  boxSizing: 'border-box',
  overflow: 'hidden',
};

const PageHeader = ({ data, compact }: { data: FieldTimeReportData; compact?: boolean }) => (
  <div>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        {data.logoURL && (
          <img
            src={data.logoURL}
            alt=""
            style={{ width: compact ? 28 : 40, height: compact ? 28 : 40, borderRadius: 8, objectFit: 'cover' }}
          />
        )}
        <div>
          <p className="font-black uppercase tracking-widest" style={{ fontSize: compact ? 11 : 13, color: INK }}>
            {data.teamName}
          </p>
          <p className="font-bold uppercase tracking-widest" style={{ fontSize: 10, color: SECONDARY }}>
            {data.seasonLabel} Season
          </p>
        </div>
      </div>
      <p className="font-bold" style={{ fontSize: 10, color: SECONDARY }}>
        Generated {data.generatedDate}
      </p>
    </div>
    <div style={{ borderBottom: `2px solid ${INK}`, marginTop: compact ? 12 : 20, paddingBottom: compact ? 10 : 16 }}>
      <p className="font-black italic uppercase" style={{ fontSize: compact ? 16 : 30, letterSpacing: '-0.01em' }}>
        Field Time Report
      </p>
      <p className="font-black uppercase" style={{ fontSize: compact ? 12 : 18, color: SECONDARY, marginTop: 2 }}>
        {data.playerNumber ? `#${data.playerNumber} ` : ''}{data.playerName}
      </p>
    </div>
  </div>
);

const PageFooter = ({ page, totalPages }: { page: number; totalPages: number }) => (
  <div
    className="flex items-center justify-between"
    style={{ borderTop: `1px solid ${LINE}`, paddingTop: 12, marginTop: 'auto' }}
  >
    <p className="font-bold uppercase tracking-widest" style={{ fontSize: 9, color: SECONDARY }}>
      Generated by Dugout
    </p>
    <p className="font-bold" style={{ fontSize: 9, color: SECONDARY }}>
      Page {page} of {totalPages}
    </p>
  </div>
);

const FactTile = ({ value, label }: { value: React.ReactNode; label: string }) => (
  <div style={{ flex: 1, border: `1px solid ${LINE}`, borderRadius: 12, padding: '14px 16px' }}>
    <p className="font-black" style={{ fontSize: 26 }}>{value}</p>
    <p className="font-bold uppercase tracking-widest" style={{ fontSize: 9, color: SECONDARY, marginTop: 2 }}>
      {label}
    </p>
  </div>
);

const positionsSummary = (row: GameFieldLogRow) =>
  row.positions.map(p => `${p.pos} (${fmtIP(p.innings)})`).join(' · ') || '—';

/** Page 1 — season summary. */
export const ReportSummaryPage = React.forwardRef<
  HTMLDivElement,
  { data: FieldTimeReportData; totalPages: number }
>(({ data, totalPages }, ref) => {
  const tracked = data.totalTrackedInnings;
  const pct = (n: number) => (tracked > 0 ? `${Math.round((n / tracked) * 100)}%` : '—');
  const ratioTotal = data.ifOf.infield + data.ifOf.outfield;
  const ifPct = ratioTotal > 0 ? Math.round((data.ifOf.infield / ratioTotal) * 100) : 0;
  const ofPct = ratioTotal > 0 ? 100 - ifPct : 0;
  const maxPosInnings = Math.max(1, ...data.positionInnings.map(p => p.innings));

  const hasMidOutfield = data.positionInnings.some(p => p.pos === 'LCF' || p.pos === 'RCF');
  const buckets = [
    { label: 'Pitcher', detail: 'P', innings: data.buckets.pitcher },
    { label: 'Catcher', detail: 'C', innings: data.buckets.catcher },
    { label: 'Infield', detail: '1B · 2B · 3B · SS', innings: data.buckets.infield },
    { label: 'Outfield', detail: hasMidOutfield ? 'LF · LCF · CF · RCF · RF' : 'LF · CF · RF', innings: data.buckets.outfield },
    { label: 'Bench', detail: 'Includes EH', innings: data.buckets.bench },
  ];

  return (
    <div ref={ref} style={PAGE_STYLE}>
      <PageHeader data={data} />

      <p className="font-bold" style={{ fontSize: 12, color: SECONDARY, marginTop: 16 }}>
        Field positions were recorded for {data.gamesTracked} of {data.totalTeamGames} completed
        games this season. {data.playerName} appears in {data.playerGamesWithData} of those games.
      </p>

      {/* Fact tiles */}
      <div className="flex gap-3" style={{ marginTop: 20 }}>
        <FactTile value={data.playerGamesWithData} label="Games with field data" />
        <FactTile value={fmtIP(tracked)} label="Innings tracked" />
        <FactTile value={fmtIP(data.fieldInnings)} label="Innings in the field" />
        <FactTile value={fmtIP(data.buckets.bench)} label="Innings on bench" />
      </div>

      {/* Five-bucket table */}
      <div style={{ marginTop: 24 }}>
        <p className="font-black uppercase tracking-widest" style={{ fontSize: 10, color: SECONDARY }}>
          Where {data.playerName.split(' ')[0]} played
        </p>
        <div style={{ marginTop: 8, border: `1px solid ${LINE}`, borderRadius: 12, overflow: 'hidden' }}>
          {buckets.map((b, i) => (
            <div
              key={b.label}
              className="flex items-center justify-between"
              style={{ padding: '9px 16px', borderTop: i > 0 ? `1px solid ${LINE}` : 'none' }}
            >
              <div className="flex items-baseline gap-2">
                <span className="font-black" style={{ fontSize: 13 }}>{b.label}</span>
                <span className="font-bold" style={{ fontSize: 10, color: SECONDARY }}>{b.detail}</span>
              </div>
              <div className="flex items-baseline gap-3">
                <span className="font-black" style={{ fontSize: 13 }}>
                  {/* Pluralise off the raw value, not the formatted string:
                      "1.0" is one inning, "1.1" is already more than one. */}
                  {fmtIP(b.innings)} {b.innings === 1 ? 'inning' : 'innings'}
                </span>
                <span className="font-bold" style={{ fontSize: 11, color: SECONDARY, width: 36, textAlign: 'right' }}>
                  {pct(b.innings)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Infield vs outfield ratio */}
      <div style={{ marginTop: 24 }}>
        <p className="font-black uppercase tracking-widest" style={{ fontSize: 10, color: SECONDARY }}>
          Infield vs. Outfield
        </p>
        {ratioTotal > 0 ? (
          <>
            <div className="flex" style={{ marginTop: 10, height: 16, borderRadius: 4, overflow: 'hidden', gap: 2 }}>
              {data.ifOf.infield > 0 && (
                <div style={{ width: `${(data.ifOf.infield / ratioTotal) * 100}%`, backgroundColor: IF_COLOR }} />
              )}
              {data.ifOf.outfield > 0 && (
                <div style={{ width: `${(data.ifOf.outfield / ratioTotal) * 100}%`, backgroundColor: OF_COLOR }} />
              )}
            </div>
            <div className="flex items-center gap-6" style={{ marginTop: 8 }}>
              <p className="flex items-center gap-2 font-bold" style={{ fontSize: 11 }}>
                <span style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: IF_COLOR, display: 'inline-block' }} />
                Infield: {fmtIP(data.ifOf.infield)} innings ({ifPct}%)
              </p>
              <p className="flex items-center gap-2 font-bold" style={{ fontSize: 11 }}>
                <span style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: OF_COLOR, display: 'inline-block' }} />
                Outfield: {fmtIP(data.ifOf.outfield)} innings ({ofPct}%)
              </p>
            </div>
            <p style={{ fontSize: 9, color: SECONDARY, marginTop: 4 }}>
              Infield includes pitcher and catcher, matching league fair-play rules. Bench innings are not included in this ratio.
            </p>
          </>
        ) : (
          <p className="font-bold" style={{ fontSize: 11, color: SECONDARY, marginTop: 8 }}>
            No innings in the field recorded yet.
          </p>
        )}
      </div>

      {/* Innings by position */}
      {data.positionInnings.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <p className="font-black uppercase tracking-widest" style={{ fontSize: 10, color: SECONDARY }}>
            Innings by position
          </p>
          <div style={{ marginTop: 10 }}>
            {data.positionInnings.map(p => (
              <div key={p.pos} className="flex items-center" style={{ marginBottom: 7 }}>
                <span className="font-bold" style={{ fontSize: 11, width: 110, color: INK }}>
                  {POSITION_LABELS[p.pos] || p.pos}
                </span>
                <div style={{ flex: 1, height: 10, backgroundColor: TRACK, borderRadius: '0 4px 4px 0' }}>
                  <div
                    style={{
                      width: `${(p.innings / maxPosInnings) * 100}%`,
                      height: '100%',
                      backgroundColor: IF_COLOR,
                      borderRadius: '0 4px 4px 0',
                    }}
                  />
                </div>
                <span className="font-black" style={{ fontSize: 11, width: 34, textAlign: 'right' }}>
                  {fmtIP(p.innings)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footnotes */}
      <div style={{ marginTop: 20 }}>
        <p style={{ fontSize: 9, color: SECONDARY }}>
          Bench innings include innings batting as Extra Hitter (EH).
          {data.hasGcGames &&
            ' Some games were imported from GameChanger, which reports total innings per position for each game.'}
        </p>
      </div>

      <PageFooter page={1} totalPages={totalPages} />
    </div>
  );
});
ReportSummaryPage.displayName = 'ReportSummaryPage';

/** Page 2+ — game-by-game position log (one chunk of rows per page). */
export const ReportGameLogPage = React.forwardRef<
  HTMLDivElement,
  { data: FieldTimeReportData; rows: GameFieldLogRow[]; page: number; totalPages: number }
>(({ data, rows, page, totalPages }, ref) => (
  <div ref={ref} style={PAGE_STYLE}>
    <PageHeader data={data} compact />

    <p className="font-black uppercase tracking-widest" style={{ fontSize: 10, color: SECONDARY, marginTop: 16 }}>
      Game-by-game position log
    </p>

    <div style={{ marginTop: 8, border: `1px solid ${LINE}`, borderRadius: 12, overflow: 'hidden' }}>
      <div className="flex" style={{ padding: '8px 16px', backgroundColor: TRACK }}>
        <span className="font-black uppercase tracking-widest" style={{ fontSize: 9, color: SECONDARY, width: 70 }}>Date</span>
        <span className="font-black uppercase tracking-widest" style={{ fontSize: 9, color: SECONDARY, width: 180 }}>Opponent</span>
        <span className="font-black uppercase tracking-widest" style={{ fontSize: 9, color: SECONDARY, flex: 1 }}>Positions (innings)</span>
        <span className="font-black uppercase tracking-widest" style={{ fontSize: 9, color: SECONDARY, width: 56, textAlign: 'right' }}>Bench</span>
      </div>
      {rows.map(row => (
        <div
          key={row.gameId}
          className="flex items-center"
          style={{ padding: '0 16px', height: 32, borderTop: `1px solid ${LINE}` }}
        >
          <span className="font-bold" style={{ fontSize: 11, width: 70 }}>{row.dateLabel}</span>
          <span
            className="font-bold"
            style={{ fontSize: 11, width: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8 }}
          >
            {row.opponent}
          </span>
          {row.unrecorded ? (
            // Deliberately NOT "Not in lineup": this game was never recorded, so
            // the report has nothing to say about whether he played it.
            <span className="font-bold italic" style={{ fontSize: 11, color: SECONDARY, flex: 1 }}>
              Not recorded <span style={{ color: SECONDARY }}>‡</span>
            </span>
          ) : row.dnp ? (
            <span className="font-bold italic" style={{ fontSize: 11, color: SECONDARY, flex: 1 }}>
              Not in lineup
            </span>
          ) : (
            <span className="font-bold" style={{ fontSize: 11, flex: 1 }}>
              {positionsSummary(row)}
              {row.source === 'gc' && <span style={{ color: SECONDARY }}> †</span>}
            </span>
          )}
          <span className="font-black" style={{ fontSize: 11, width: 56, textAlign: 'right' }}>
            {row.dnp || row.unrecorded ? '—' : fmtIP(row.benchInnings)}
          </span>
        </div>
      ))}
    </div>

    {data.hasGcGames && (
      <p style={{ fontSize: 9, color: SECONDARY, marginTop: 10 }}>
        † Imported from GameChanger: innings per position for the game. The inning-by-inning order was not recorded.
      </p>
    )}
    {data.gameLog.some(r => r.unrecorded) && (
      <p style={{ fontSize: 9, color: SECONDARY, marginTop: 4 }}>
        ‡ This game was not recorded in Dugout and no GameChanger stats were imported for it, so it is not counted in the totals above.
      </p>
    )}

    <PageFooter page={page} totalPages={totalPages} />
  </div>
));
ReportGameLogPage.displayName = 'ReportGameLogPage';
